/** @format */

import { inject, Injectable, OnDestroy, PLATFORM_ID, signal } from '@angular/core';
import { BreakPointNew } from '@shared/data-access/enums';
import { isPlatformBrowser } from '@angular/common';
import { BREAKPOINT_NEW_VALUES } from '@shared/data-access/constants';
import { BreakpointMediaQuery } from '@shared/data-access/interfaces';

@Injectable({
  providedIn: 'root',
})
export class BreakpointsService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);

  public readonly breakpoint = signal(BreakPointNew.EXTRAEXTRASMALL);

  private readonly cleanupFunctions: (() => void)[] = [];

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.setupMediaQueryListeners();
    }
  }

  private setupMediaQueryListeners(): void {
    if (typeof window.matchMedia !== 'function') return;

    const mediaQueries = this.getMediaQueries();
    mediaQueries.forEach(({ breakpoint, query }) => {
      const mediaQuery = window.matchMedia(query);

      if (mediaQuery.matches) {
        this.breakpoint.set(breakpoint);
      }

      const onChange = (event: MediaQueryListEvent) => {
        if (event.matches) {
          this.breakpoint.set(breakpoint);
        }
      };

      mediaQuery.addEventListener('change', onChange);
      this.cleanupFunctions.push(() => mediaQuery.removeEventListener('change', onChange));
    });
  }

  private getMediaQueries(): BreakpointMediaQuery[] {
    const breakpoints = BREAKPOINT_NEW_VALUES;
    const xs = breakpoints[BreakPointNew.EXTRASMALL];
    const s = breakpoints[BreakPointNew.SMALL];
    const m = breakpoints[BreakPointNew.MEDIUM];
    const l = breakpoints[BreakPointNew.LARGE];
    const xl = breakpoints[BreakPointNew.EXTRALARGE];
    const xxl = breakpoints[BreakPointNew.EXTRAEXTRALARGE];
    const tinyAmount = 0.02; // Allows us to cover the gap between max and min widths incl. fractional pixels

    return [
      {
        breakpoint: BreakPointNew.EXTRAEXTRASMALL,
        query: `(max-width: ${xs - tinyAmount}px)`,
      },
      {
        breakpoint: BreakPointNew.EXTRASMALL,
        query: `(min-width: ${xs}px) and (max-width: ${s - tinyAmount}px)`,
      },
      {
        breakpoint: BreakPointNew.SMALL,
        query: `(min-width: ${s}px) and (max-width: ${m - tinyAmount}px)`,
      },
      {
        breakpoint: BreakPointNew.MEDIUM,
        query: `(min-width: ${m}px) and (max-width: ${l - tinyAmount}px)`,
      },
      {
        breakpoint: BreakPointNew.LARGE,
        query: `(min-width: ${l}px) and (max-width: ${xl - tinyAmount}px)`,
      },
      {
        breakpoint: BreakPointNew.EXTRALARGE,
        query: `(min-width: ${xl}px) and (max-width: ${xxl - tinyAmount}px)`,
      },
      {
        breakpoint: BreakPointNew.EXTRAEXTRALARGE,
        query: `(min-width: ${xxl}px)`,
      },
    ];
  }

  ngOnDestroy(): void {
    this.cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
  }
}
